import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Button, Spinner, Container, Row, Col,
    Input, InputGroup, Table
} from "reactstrap";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import DataTable from "react-data-table-component";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { H3, P } from "../../AbstractElements";
import { Search, RotateCcw, FileText, Download } from 'react-feather';
import DatePicker from "react-datepicker";
import { useConfig } from "../../Context/ConfigContext";
import { useAuth } from "../../Context/AuthContext";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

interface BookingData {
    id: number;
    patient_name: string;
    patient_mobile: string;
    date: string;
    actual_start_at: string;
    actual_end_at: string;
    total_amount: number;
    bystander_total_amount: number;
    bystander: {
        id: number;
        name: string;
        mobile?: string;
        phone_number?: string;
    };
    user?: {
        id: number;
        name: string;
    };
}

interface BookingDataWithFooter extends Partial<BookingData> {
    isFooter?: boolean;
    total_duration_display?: string;
}



export default function ServiceReport() {
    const [data, setData] = useState<BookingData[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("date");
    const [sortDir, setSortDir] = useState("desc");
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [summary, setSummary] = useState({ total_user_amount: 0, total_bystander_earning: 0 });
    const [pdfLoading, setPdfLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const { config } = useConfig();
    const { hasPermission } = useAuth();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            const response: any = await apiPost(`report/service-report?page=${currentPage}`, {
                per_page: perPage,
                search: debouncedSearch,
                start_date: startDate,
                end_date: endDate,
                sort_by: sortBy,
                sort_direction: sortDir
            });
            setData(response.data.data || []);
            setTotalRows(response.data.total || 0);
            setSummary(response.summary || { total_user_amount: 0, total_bystander_earning: 0 });
        } catch (error) {
            toast.error("Failed to load service report");
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage, debouncedSearch, startDate, endDate, sortBy, sortDir]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, startDate, endDate]);





    const handleSort = (column: any, sortDirection: string) => {
        setSortBy(column.sortField || 'date');
        setSortDir(sortDirection);
    };

    const calculateDuration = (start: string, end: string) => {
        if (!start || !end) return "0s";
        const s = new Date(start).getTime();
        const e = new Date(end).getTime();
        const diff = Math.max(0, e - s) / 1000; // seconds

        if (diff < 60) return `${Math.floor(diff)}s`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ${Math.floor(diff % 60)}s`;
        return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
    };

    const fetchAllData = async () => {
        try {
            const response: any = await apiPost(`report/service-report?page=1`, {
                per_page: -1,
                search: debouncedSearch,
                start_date: startDate,
                end_date: endDate,
                sort_by: sortBy,
                sort_direction: sortDir
            });
            return response.data.data || [];
        } catch (error) {
            console.error("Error fetching all data:", error);
            return [];
        }
    };

    const handleReset = () => {
        setSearchTerm("");
        setStartDate("");
        setEndDate("");
        setCurrentPage(1);
    };

    const calculateTotalDuration = (exportDataList: any[]) => {
        let totalSeconds = 0;
        exportDataList.forEach(item => {
            if (item.actual_start_at && item.actual_end_at) {
                const s = new Date(item.actual_start_at).getTime();
                const e = new Date(item.actual_end_at).getTime();
                totalSeconds += Math.max(0, e - s) / 1000;
            }
        });

        if (totalSeconds < 60) return `${Math.floor(totalSeconds)}s`;
        if (totalSeconds < 3600) return `${Math.floor(totalSeconds / 60)}m ${Math.floor(totalSeconds % 60)}s`;
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        return `${h}h ${m}m`;
    };

    const formatDateForExport = (dateString: string) => {
        if (!dateString) return "N/A";
        const dateObj = new Date(dateString);
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = String(dateObj.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
    };

    const exportToExcel = async (exportDataItems: any[]) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Service Report');

        // Header Style
        const headerStyle: Partial<ExcelJS.Style> = {
            font: { bold: true, color: { argb: 'FFFFFFFF' } },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00695C' } },
            alignment: { vertical: 'middle', horizontal: 'center' }
        };

        worksheet.columns = [
            { header: 'Sl.No', key: 'sl', width: 8 },
            { header: 'Patient Info', key: 'patient', width: 40 },
            { header: 'Bystander Info', key: 'bystander', width: 35 },
            { header: 'Service Schedule', key: 'schedule', width: 40 },
            { header: `Patient Amount (${config.currency})`, key: 'p_amt', width: 15 },
            { header: `Bystander Amount (${config.currency})`, key: 'b_amt', width: 15 },
        ];

        // Apply Header Style
        worksheet.getRow(1).eachCell((cell) => {
            cell.style = headerStyle;
        });

        exportDataItems.forEach((item, index) => {
            const bystanderMobile = item.bystander?.phone_number || item.bystander?.mobile || "";
            const startTime = item.actual_start_at ? new Date(item.actual_start_at).toLocaleTimeString([],
 { hour: '2-digit', minute: '2-digit' }) : '';
            const endTime = item.actual_end_at ? new Date(item.actual_end_at).toLocaleTimeString([], 
{ hour: '2-digit', minute: '2-digit' }) : '';
            const timeRange = startTime && endTime ? `Time: ${startTime} - ${endTime}` : "";

            const row = worksheet.addRow({
                sl: index + 1,
                patient: `${item.patient_name}\n${item.patient_mobile}\nBooked by: ${item.user?.name || "N/A"}`,
                bystander: `${item.bystander?.name || "N/A"}${bystanderMobile ? "\n" + bystanderMobile : ""}`,
                schedule: `${formatDateForExport(item.date)}\n${timeRange}\nDuration:
 ${calculateDuration(item.actual_start_at, item.actual_end_at)}`,
                p_amt: item.total_amount,
                b_amt: item.bystander_total_amount
            });

            // Set Alignment with Wrap Text
            row.eachCell((cell) => {
                cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            });
        });

        // Add Summary/Footer Row
        const summaryRow = worksheet.addRow({
            sl: '',
            patient: '',
            bystander: '',
            schedule: `TOTAL DURATION: ${calculateTotalDuration(exportDataItems)}`,
            p_amt: summary.total_user_amount,
            b_amt: summary.total_bystander_earning
        });

        summaryRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        });

        // Generate and Save
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Service_Report_${formatDate(new Date())}.xlsx`);
    };

    const exportToPDF = (exportDataItems: any[]) => {
        const doc = new jsPDF('p', 'mm', 'a4'); // Switch back to portrait if 6 columns
        doc.text("Service Report", 14, 15);

        const currencyMap: Record<string, string> = { 
            '₹': 'Rs.', '€': 'EUR', '£': 'GBP', '¥': 'YEN',
            '₽': 'RUB', '₩': 'KRW', '฿': 'THB', '₪': 'ILS', '₫': 'VND', '₱': 'PHP' 
        };
        const pdfCurrency = currencyMap[config.currency] || config.currency;
        const tableColumn = ["Sl.No", "Patient Info", "Bystander Info", "Service Schedule", `Patient Amount (${pdfCurrency})`, 
`Bystander Amount (${pdfCurrency})`];
        const tableRows = exportDataItems.map((item, index) => {
            const bystanderMobile = item.bystander?.phone_number || item.bystander?.mobile || "";
            const startTime = item.actual_start_at ? new Date(item.actual_start_at).toLocaleTimeString([], 
{ hour: '2-digit', minute: '2-digit' }) : '';
            const endTime = item.actual_end_at ? new Date(item.actual_end_at).toLocaleTimeString([],
 { hour: '2-digit', minute: '2-digit' }) : '';
            const timeRange = startTime && endTime ? `Time: ${startTime} - ${endTime}` : "";

            return [
                index + 1,
                `${item.patient_name}\n${item.patient_mobile}\nBooked by: ${item.user?.name || "N/A"}`,
                `${item.bystander?.name || "N/A"}${bystanderMobile ? "\n" + bystanderMobile : ""}`,
                `${formatDateForExport(item.date)}\n${timeRange}\nDuration: 
${calculateDuration(item.actual_start_at, item.actual_end_at)}`,
                item.total_amount,
                item.bystander_total_amount
            ];
        });

        // Add Totals Footer Row to PDF
        tableRows.push([
            "",
            "",
            "",
            `TOTAL DURATION: ${calculateTotalDuration(exportDataItems)}`,
            summary.total_user_amount,
            summary.total_bystander_earning
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
            theme: 'grid',
            headStyles: { fillColor: [0, 105, 92] },
            styles: { fontSize: 9, cellPadding: 3 },
            columnStyles: {
                0: { cellWidth: 15 },
                4: { fontStyle: 'bold', halign: 'center' },
                5: { fontStyle: 'bold', halign: 'center', textColor: [0, 105, 92] }
            },
            didParseCell: (cellData: any) => {
                if (cellData.row.index === tableRows.length - 1) {
                    cellData.cell.styles.fontStyle = 'bold';
                    cellData.cell.styles.fillColor = [245, 245, 245];
                }
            }
        });
        doc.save(`Service_Report_${formatDate(new Date())}.pdf`);
    };



    const columns: any[] = [
        {
            name: <div><span className="fw-bold">Sl. No.</span></div>,
            selector: (row: BookingDataWithFooter, index: number) =>
                row.isFooter ? "" : (currentPage - 1) * perPage + (index ?? 0) + 1,
            width: "100px",
        },
        {
            name: <div><span className="fw-bold">Patient Info</span></div>,
            cell: (row: BookingDataWithFooter) => {
                if (row.isFooter) return "";
                return (
                    <div className="py-2">
                        <div className="fw-bold text-dark">{row.patient_name || "N/A"}</div>
                        <div className="text-muted small" style={{ fontSize: '11px' }}>{row.patient_mobile}</div>
                        <div className="mt-1 pt-1 border-top" style={{ borderStyle: 'dotted', borderColor: '#e5e7eb' }}>
                            <span className="text-primary fw-bold" style={{ fontSize: '10px' }}>BOOKED BY: </span>
                            <span className="text-dark fw-bold" style={{ fontSize: '10px' }}>{row.user?.name || "N/A"}</span>
                        </div>
                    </div>
                );
            },
            sortable: (row: BookingDataWithFooter) => !row.isFooter,
            sortField: "patient_name"
        },
        {
            name: <div><span className="fw-bold">Bystander Info</span></div>,
            cell: (row: BookingDataWithFooter) => {
                if (row.isFooter) return <span className="fw-bold" style={{ fontSize: '14px', color: '#00695c' }}>TOTAL:</span>;
                const bystanderMobile = row.bystander?.phone_number || row.bystander?.mobile || (row as any).bystander_mobile;
                return (
                    <div className="py-2">
                        <div className="fw-bold text-dark">{row.bystander?.name || "N/A"}</div>
                        {bystanderMobile && (
                            <div className="text-muted small" style={{ fontSize: '11px' }}>{bystanderMobile}</div>
                        )}
                    </div>
                );
            },
            sortable: (row: BookingDataWithFooter) => !row.isFooter,
            sortField: "bystander.name"
        },
        {
            name: <div><span className="fw-bold">Service Schedule</span></div>,
            cell: (row: BookingDataWithFooter) => {
                if (row.isFooter) return <span className="fw-bold" 
style={{ fontSize: '14px', color: '#00695c' }}>{row.total_duration_display}</span>;
                if (!row.date) return 'N/A';

                const dateObj = new Date(row.date);
                const day = String(dateObj.getDate()).padStart(2, '0');
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const year = String(dateObj.getFullYear()).slice(-2);
                const dateStr = `${day}/${month}/${year}`;
                const startTime = row.actual_start_at ? new Date(row.actual_start_at).toLocaleTimeString([], 
{ hour: '2-digit', minute: '2-digit' }) : '';
                const endTime = row.actual_end_at ? new Date(row.actual_end_at).toLocaleTimeString([],
 { hour: '2-digit', minute: '2-digit' }) : '';
                const duration = calculateDuration(row.actual_start_at || "", row.actual_end_at || "");

                return (
                    <div className="small py-2">
                        <div className="fw-bold mb-1 text-dark">{dateStr}</div>
                        <div className="text-muted mb-1" style={{ fontSize: '11px' }}>
                            <span className="fw-bold text-uppercase">Time:</span> {startTime} - {endTime}
                        </div>
                        <div className="text-info" style={{ fontSize: '11px' }}>
                            <span className="fw-bold text-uppercase">Duration:</span> {duration}
                        </div>
                    </div>
                );
            },
            sortable: (row: BookingDataWithFooter) => !row.isFooter,
            sortField: "date",
            width: "220px"
        },
        {
            name: <div><span className="fw-bold">Patient Amount ({config.currency})</span></div>,
            sortable: (row: BookingDataWithFooter) => !row.isFooter,
            sortField: "total_amount",
            cell: (row: BookingDataWithFooter) => (
                <div className="w-100 fw-bold" style={{ fontSize: row.isFooter ? '14px' : 'inherit', color: row.isFooter ? '#00695c' : 'inherit' }}>
                    {Number(row.total_amount || 0).toLocaleString()}
                </div>
            )
        },
        {
            name: <div><span className="fw-bold">Bystander Amount ({config.currency})</span></div>,
            sortable: (row: BookingDataWithFooter) => !row.isFooter,
            sortField: "bystander_total_amount",
            cell: (row: BookingDataWithFooter) => (
                <div className={row.isFooter ? "w-100 fw-bold px-3" : "w-100 fw-bold text-success px-3"} style={{ fontSize: row.isFooter ? '14px' : 'inherit', color: row.isFooter ? '#00695c' : 'inherit' }}>
                    {Number(row.bystander_total_amount || 0).toLocaleString()}
                </div>
            )
        },
    ];

    const calculateOverallTotalTime = () => {
        // Since we are getting summary from backend, we might not need to calculate duration manually
        // but if we wanted to show total duration of VISIBLE items:
        const totalSec = data.reduce((acc, row) => {
            if (!row.actual_start_at || !row.actual_end_at) return acc;
            return acc + (Math.max(0, new Date(row.actual_end_at).getTime() - new Date(row.actual_start_at).getTime()) / 1000);
        }, 0);

        if (totalSec < 3600) return `${Math.floor(totalSec / 60)}m ${Math.floor(totalSec % 60)}s`;
        return `${Math.floor(totalSec / 3600)}h ${Math.floor((totalSec % 3600) / 60)}m`;
    };

    const columnsData: BookingDataWithFooter[] = data.length > 0 ? [
        ...data,
        {
            isFooter: true,
            total_amount: summary.total_user_amount,
            bystander_total_amount: summary.total_bystander_earning,
            total_duration_display: calculateOverallTotalTime()
        }
    ] : [];

    const conditionalRowStyles = [
        {
            when: (row: BookingDataWithFooter) => !!row.isFooter,
            style: {
                fontWeight: "800",
                backgroundColor: "#f8f9fa",
                color: "#1e3a8a",
                borderTop: "2px solid #dee2e6",
                fontSize: "16px",
            },
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                color: '#308e87',
                minHeight: '50px',
                borderBottom: '2px solid #308e87',
            },
        },
        headCells: {
            style: { fontSize: '16px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px', borderBottom: "1px solid #dee2e6" },
        },
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Service Report" parent="Reports" title="Audit Stream" />
            <Container fluid>
                <Card className="border-0 shadow-sm">
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <H3 className="text-primary mb-0">Service Report</H3>
                            <div className="d-flex gap-2">
                                {hasPermission('service-report.download_pdf') && (
                                    <Button
                                        color="success"
                                        onClick={async () => {
                                            setPdfLoading(true);
                                            const allData = await fetchAllData();
                                            exportToPDF(allData);
                                            setPdfLoading(false);
                                        }}
                                        disabled={pdfLoading}
                                        className="d-flex align-items-center gap-2 px-3 shadow-sm border-0"
                                        style={{ borderRadius: '6px' }}
                                        title="Export PDF"
                                    >
                                        {pdfLoading ? <Spinner size="sm" /> : <FileText size={16} />}
                                        <span className="fw-bold small">{pdfLoading ? 'PREPARING...' : 'PDF'}</span>
                                    </Button>
                                )}
                                {hasPermission('service-report.download_excel') && (
                                    <Button
                                        color="warning"
                                        onClick={async () => {
                                            setExcelLoading(true);
                                            const allData = await fetchAllData();
                                            await exportToExcel(allData);
                                            setExcelLoading(false);
                                        }}
                                        disabled={excelLoading}
                                        className="d-flex align-items-center gap-2 px-3 shadow-sm border-0 text-white"
                                        style={{ backgroundColor: '#ffaf00', borderRadius: '6px' }}
                                        title="Export Excel"
                                    >
                                        {excelLoading ? <Spinner size="sm" /> : <Download size={16} />}
                                        <span className="fw-bold small">{excelLoading ? 'PREPARING...' : 'EXCEL'}</span>
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Filter Dashboard - Full width bar with content aligned to the right */}
                        <div className=" mb-4">
                            <Row className="align-items-center justify-content-end g-3">
                                {/* Date Filters - Compact */}
                                <Col xl={5} lg={7} md={12} className="d-flex align-items-center gap-2">
                                    <span className="small text-muted fw-bold text-uppercase ms-2" 
style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>FROM:</span>
                                    <div style={{ flex: 1 }}>
                                        <DatePicker
                                            selected={startDate ? new Date(startDate) : null}
                                            onChange={(date: Date) => setStartDate(date ? formatDate(date) : "")}
                                            placeholderText="Start date"
                                            className="form-control bg-white"
                                            dateFormat="yyyy-MM-dd"
                                            isClearable
                                        />
                                    </div>
                                    <span className="small text-muted fw-bold text-uppercase" 
style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>TO:</span>
                                    <div style={{ flex: 1 }}>
                                        <DatePicker
                                            selected={endDate ? new Date(endDate) : null}
                                            onChange={(date: Date) => setEndDate(date ? formatDate(date) : "")}
                                            placeholderText="End date"
                                            className="form-control bg-white"
                                            dateFormat="yyyy-MM-dd"
                                            isClearable
                                        />
                                    </div>
                                </Col>

                                {/* Combined Search - Compact */}
                                <Col xl={3} lg={5} md={12}>
                                    <InputGroup className="shadow-sm">
                                        <Input
                                            placeholder="Search..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="bg-white"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </InputGroup>
                                </Col>


                            </Row>
                        </div>

                        <div className="border rounded">
                            <DataTable
                                columns={columns}
                                data={columnsData}
                                progressPending={loading}
                                progressComponent={<div className="d-flex justify-content-center align-items-center w-100"
 style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                onChangeRowsPerPage={(newSize: number, page: number) => setPerPage(newSize)}
                                onChangePage={(page: number) => setCurrentPage(page)}
                                sortServer
                                onSort={handleSort}
                                highlightOnHover
                                customStyles={customStyles}
                                conditionalRowStyles={conditionalRowStyles}
                                noDataComponent={
                                    <div className="d-flex justify-content-center align-items-center w-100" 
style={{ minHeight: "50vh" }}>
                                        <Card className="shadow-sm border-0 text-center" 
style={{ maxWidth: "320px", width: "100%" }}>
                                            <CardBody>
                                                <h6 className="fw-semibold mb-1">No Records Found</h6>
                                                <small className="text-muted">No services matching your criteria.</small>
                                            </CardBody>
                                        </Card>
                                    </div>
                                }
                            />
                        </div>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
